import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

const ParticlesEffect = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Basic Three.js setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );

    const renderer = new THREE.WebGLRenderer({ alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    document.body.appendChild(renderer.domElement);

    // Array to hold the particles
    const particles = [];
    const particlePool = []; // Particle pool for reusing particles

    // Create a particle geometry (circle)
    const particleGeometry = new THREE.CircleGeometry(0.02, 16);

    // Material for particles (white color)
    const particleMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });

    // Function to create new particles or reuse from the pool
    function createParticle() {
      const particle =
        particlePool.length > 0
          ? particlePool.pop()
          : new THREE.Mesh(particleGeometry, particleMaterial);
      particle.position.set(
        Math.random() * 10 - 5,
        Math.random() * 10 - 5,
        particles.length * 0.35 // Spacing the particles along the Z-axis to create the tunnel effect
      );

      // Add velocity property to each particle to simulate gravitational attraction
      particle.velocity = new THREE.Vector3(0, 0, 0);

      particles.push(particle);
      scene.add(particle);
    }

    // Initial particle creation
    for (let i = 0; i < 2000; i++) {
      createParticle();
    }

    // Set up basic lighting
    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);

    // Set camera position
    camera.position.z = 15; // Move the camera further away

    // Parallax effect variables
    const mouse = new THREE.Vector2();
    const windowHalf = new THREE.Vector2(
      window.innerWidth / 2,
      window.innerHeight / 2
    );
    const parallaxFactor = 0.2; // Adjust this value to control the intensity of the parallax effect
    // Event listener for mouse movement

    document.addEventListener("mousemove", onDocumentMouseMove, false);

    function onDocumentMouseMove(event) {
      mouse.x = (event.clientX - windowHalf.x) / windowHalf.x;
      mouse.y = (event.clientY - windowHalf.y) / windowHalf.y;

      // Adjust the container's position based on mouse movement (opposite direction)
      const container = document.querySelector(".container");
      container.style.transform = `translateX(${
        mouse.x * -30
      }px) translateY(${mouse.y}vh)`; // Adjust the -30 value to control the up and down movement intensity
    }

    // Function to update the particle position based on gravitational attraction
    function updateParticlePosition(particle) {
      const boxCenter = new THREE.Vector3(0, 7, -5); // Center of the div box (adjust z-coordinate as needed)

      // Calculate the direction vector from the particle to the box center
      const direction = boxCenter.clone().sub(particle.position);

      // Calculate the distance between the particle and the box center
      const distance = direction.length();

      // Apply gravitational force based on the distance (inverse square law)
      const force = 2 / (distance * distance); // Adjust the force value as needed

      // Normalize the direction vector to apply force in the direction
      direction.normalize();

      // Apply the force to the particle velocity (adjust the multiplier for intensity)
      particle.velocity.add(direction.multiplyScalar(force * 0.01));

      // Update the particle position based on the velocity
      particle.position.add(particle.velocity);
    }

    // Animation loop
    function animate() {
      requestAnimationFrame(animate);

      // Adjust camera position based on mouse movement
      camera.position.x = mouse.x * 0.5;
      camera.position.y = -mouse.y * 0.5;

      // Move particles along the negative Z-axis (opposite direction) with slower speed
      for (let i = 0; i < particles.length; i++) {
        particles[i].position.z -= 0.00001;

        // Update particle position based on gravitational attraction towards the div box
        updateParticlePosition(particles[i]);

        // Remove particles that are out of view
        if (particles[i].position.z < 1) {
          scene.remove(particles[i]);
          particlePool.push(particles[i]); // Add the particle back to the pool for reuse
          particles.splice(i, 1);
          createParticle(); // Create a new particle at the end of the tunnel
        }
      }

      renderer.render(scene, camera);
    }

    animate();
  }, []);

  return <div className="container" ref={containerRef}></div>;
};

export default ParticlesEffect;
